import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { HiltiMaterial, HiltiMaterialReceipt, HiltiProduct } from '../types';
import {
  hiltiMaterialReceiptsAPI,
  hiltiMaterialsAPI,
  hiltiProductsAPI
} from '../services/api';
import { showCustomAlert } from './CustomAlert';

interface FormState {
  productId: string;
  materialId: string;
  receiptDate: Date | null;
  receiptTime: string;
  quantity: number;
  unit: string;
  supplier: string;
  transporter: string;
  reference: string;
  notes: string;
}

const HiltiMaterialReceipts: React.FC = () => {
  const [products, setProducts] = useState<HiltiProduct[]>([]);
  const [materialsByProduct, setMaterialsByProduct] = useState<Record<string, HiltiMaterial[]>>({});
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [selectedMaterialId, setSelectedMaterialId] = useState<string>('');
  const [receipts, setReceipts] = useState<HiltiMaterialReceipt[]>([]);
  const [loading, setLoading] = useState(false);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<HiltiMaterialReceipt | null>(null);
  const [error, setError] = useState('');

  const [form, setForm] = useState<FormState>({
    productId: '',
    materialId: '',
    receiptDate: new Date(),
    receiptTime: '',
    quantity: 0,
    unit: '',
    supplier: '',
    transporter: '',
    reference: '',
    notes: ''
  });

  const timeOptions = useMemo(() => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute
          .toString()
          .padStart(2, '0')}`;
        options.push({ label: timeString, value: timeString });
      }
    }
    return options;
  }, []);

  const loadProducts = async () => {
    try {
      const data = await hiltiProductsAPI.getAll();
      setProducts(data);
      if (!selectedProductId && data.length > 0) {
        setSelectedProductId(data[0]._id);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri učitavanju proizvoda';
      showCustomAlert.error(msg);
    }
  };

  const loadMaterials = async (productId: string) => {
    if (!productId || materialsByProduct[productId]) return;
    try {
      const mats = await hiltiMaterialsAPI.getAllForProduct(productId);
      setMaterialsByProduct(prev => ({ ...prev, [productId]: mats }));
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || 'Greška pri učitavanju materijala';
      showCustomAlert.error(msg);
    }
  };

  const loadReceipts = async () => {
    if (!selectedProductId) return;
    setLoading(true);
    try {
      const data = await hiltiMaterialReceiptsAPI.getAll({
        productId: selectedProductId,
        materialId: selectedMaterialId || undefined
      });
      setReceipts(data);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri učitavanju prijema';
      showCustomAlert.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadProducts();
  }, []);

  useEffect(() => {
    if (selectedProductId) {
      void loadMaterials(selectedProductId);
      void loadReceipts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductId, selectedMaterialId]);

  const materialOptions = useMemo(() => {
    if (!selectedProductId) return [];
    return (materialsByProduct[selectedProductId] || []).map(m => ({
      label: `${m.name} (${m.unit})`,
      value: m._id
    }));
  }, [selectedProductId, materialsByProduct]);

  const productOptions = useMemo(
    () => products.map(p => ({ label: p.name, value: p._id })),
    [products]
  );

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const qtyBody = (r: HiltiMaterialReceipt) => `${r.quantity} ${r.unit}`;
  const lastUpdateBody = (r: HiltiMaterialReceipt) => {
    const iso = r.updatedAt || r.createdAt;
    if (!iso) return '—';
    return new Date(iso).toLocaleString('sr-RS');
  };

  const openCreate = () => {
    setEditing(null);
    setError('');
    setForm({
      productId: selectedProductId,
      materialId: selectedMaterialId || '',
      receiptDate: new Date(),
      receiptTime: '',
      quantity: 0,
      unit: '',
      supplier: '',
      transporter: '',
      reference: '',
      notes: ''
    });
    setDialogVisible(true);
  };

  const openEdit = (r: HiltiMaterialReceipt) => {
    setEditing(r);
    setError('');
    setForm({
      productId: typeof r.productId === 'string' ? r.productId : r.productId._id,
      materialId: typeof r.materialId === 'string' ? r.materialId : r.materialId._id,
      receiptDate: new Date(r.receiptDate),
      receiptTime: r.receiptTime,
      quantity: r.quantity,
      unit: r.unit,
      supplier: r.supplier || '',
      transporter: r.transporter || '',
      reference: r.reference || '',
      notes: r.notes || ''
    });
    setDialogVisible(true);
  };

  const handleSubmit = async () => {
    if (!form.productId || !form.materialId || !form.receiptDate || !form.receiptTime) {
      setError('Proizvod, materijal, datum i vreme su obavezni');
      return;
    }
    if (form.quantity <= 0) {
      setError('Količina mora biti veća od nule');
      return;
    }

    const payload = {
      productId: form.productId,
      materialId: form.materialId,
      receiptDate: form.receiptDate.toISOString().split('T')[0],
      receiptTime: form.receiptTime,
      quantity: form.quantity,
      unit: form.unit || undefined,
      supplier: form.supplier || undefined,
      transporter: form.transporter || undefined,
      reference: form.reference || undefined,
      notes: form.notes || undefined
    };

    try {
      setError('');
      if (editing?._id) {
        await hiltiMaterialReceiptsAPI.update(editing._id, payload);
        showCustomAlert.success('Prijem je ažuriran');
      } else {
        await hiltiMaterialReceiptsAPI.create(payload);
        showCustomAlert.success('Prijem je dodat');
      }
      setDialogVisible(false);
      await loadReceipts();
      await loadMaterials(form.productId);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri čuvanju prijema';
      setError(msg);
      showCustomAlert.error(msg);
    }
  };

  const deleteReceipt = async (r: HiltiMaterialReceipt) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovaj prijem?')) return;
    try {
      await hiltiMaterialReceiptsAPI.delete(r._id);
      await loadReceipts();
      await loadMaterials(
        typeof r.productId === 'string' ? r.productId : (r.productId as HiltiProduct)._id
      );
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri brisanju prijema';
      showCustomAlert.error(msg);
    }
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      <Card title="Hilti Zaprimanje materijala">
        <div className="flex flex-column md:flex-row gap-3 mb-3">
          <div className="flex flex-column gap-1" style={{ minWidth: '220px' }}>
            <span style={{ fontWeight: 600 }}>Proizvod</span>
            <Dropdown
              value={selectedProductId}
              options={productOptions}
              onChange={(e) => setSelectedProductId(e.value)}
              placeholder="Izaberite proizvod"
            />
          </div>
          <div className="flex flex-column gap-1" style={{ minWidth: '220px' }}>
            <span style={{ fontWeight: 600 }}>Filtriraj po materijalu</span>
            <Dropdown
              value={selectedMaterialId}
              options={[{ label: 'Svi materijali', value: '' }, ...materialOptions]}
              onChange={(e) => setSelectedMaterialId(e.value)}
              placeholder="Svi materijali"
              disabled={!selectedProductId}
            />
          </div>
          <div className="flex align-items-end gap-2">
            <Button
              label="Osveži"
              icon="pi pi-refresh"
              outlined
              onClick={loadReceipts}
              disabled={!selectedProductId}
            />
            <Button
              label="Dodaj prijem"
              icon="pi pi-plus"
              onClick={openCreate}
              disabled={!selectedProductId}
            />
          </div>
        </div>

        {error && !dialogVisible && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={error} />
          </div>
        )}

        <DataTable
          value={receipts}
          loading={loading}
          paginator
          rows={15}
          rowsPerPageOptions={[15, 30, 50]}
          responsiveLayout="scroll"
          emptyMessage="Nema prijema materijala"
        >
          <Column field="materialName" header="Materijal" sortable />
          <Column header="Količina" body={qtyBody} />
          <Column field="notes" header="Napomena" />
          <Column
            field="receiptDate"
            header="Datum"
            body={(r: HiltiMaterialReceipt) => fmtDate(r.receiptDate)}
            sortable
          />
          <Column field="receiptTime" header="Vreme" sortable />
          <Column
            header="Poslednja izmena"
            body={lastUpdateBody}
            style={{ width: '200px' }}
          />
          <Column
            header="Akcije"
            style={{ width: '180px' }}
            body={(r: HiltiMaterialReceipt) => (
              <div className="flex gap-2 justify-content-end">
                <Button
                  icon="pi pi-pencil"
                  className="action-icon-btn"
                  text
                  rounded
                  onClick={() => openEdit(r)}
                  tooltip="Izmeni"
                />
                <Button
                  icon="pi pi-trash"
                  className="action-icon-btn"
                  text
                  rounded
                  severity="danger"
                  onClick={() => deleteReceipt(r)}
                  tooltip="Obriši"
                />
              </div>
            )}
          />
        </DataTable>
      </Card>

      <Dialog
        header={editing ? 'Izmeni prijem' : 'Novi prijem'}
        visible={dialogVisible}
        style={{ width: '760px', maxWidth: '96vw' }}
        onHide={() => setDialogVisible(false)}
        modal
        closable
      >
        {error && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={error} />
          </div>
        )}

        <div className="p-fluid grid formgrid">
          <div className="col-12 md:col-6">
            <div className="field">
              <label style={{ fontWeight: 600 }}>Proizvod*</label>
              <Dropdown
                value={form.productId}
                options={productOptions}
                onChange={(e) => {
                  const value = e.value as string;
                  setForm(prev => ({ ...prev, productId: value, materialId: '' }));
                  setSelectedProductId(value);
                  setSelectedMaterialId('');
                }}
                placeholder="Izaberite proizvod"
                className="w-full"
                appendTo="self"
              />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="field">
              <label style={{ fontWeight: 600 }}>Materijal*</label>
              <Dropdown
                value={form.materialId}
                options={materialOptions}
                onChange={(e) =>
                  setForm(prev => ({ ...prev, materialId: e.value as string }))
                }
                placeholder="Izaberite materijal"
                disabled={!form.productId}
                className="w-full"
                appendTo="self"
              />
            </div>
          </div>

          <div className="col-12 md:col-6">
            <div className="field">
              <label style={{ fontWeight: 600 }}>Datum*</label>
              <Calendar
                value={form.receiptDate}
                onChange={(e) =>
                  setForm(prev => ({ ...prev, receiptDate: e.value as Date | null }))
                }
                dateFormat="dd.mm.yy"
                showIcon
                className="w-full"
              />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="field">
              <label style={{ fontWeight: 600 }}>Vreme*</label>
              <Dropdown
                value={form.receiptTime}
                options={timeOptions}
                onChange={(e) =>
                  setForm(prev => ({ ...prev, receiptTime: e.value as string }))
                }
                placeholder="Izaberite vreme"
                className="w-full"
                appendTo="self"
              />
            </div>
          </div>

          <div className="col-12 md:col-6">
            <div className="field">
              <label style={{ fontWeight: 600 }}>Količina*</label>
              <InputNumber
                value={form.quantity}
                onValueChange={(e) =>
                  setForm(prev => ({ ...prev, quantity: e.value || 0 }))
                }
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={3}
                className="w-full"
              />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="field">
              <label style={{ fontWeight: 600 }}>Jedinica</label>
              <InputText
                value={form.unit}
                onChange={(e) =>
                  setForm(prev => ({ ...prev, unit: e.target.value }))
                }
                placeholder="Ostavi prazno za podrazumevanu jedinicu"
                className="w-full"
              />
            </div>
          </div>

          <div className="col-12 md:col-6">
            <div className="field">
              <label style={{ fontWeight: 600 }}>Dobavljač</label>
              <InputText
                value={form.supplier}
                onChange={(e) =>
                  setForm(prev => ({ ...prev, supplier: e.target.value }))
                }
                className="w-full"
              />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="field">
              <label style={{ fontWeight: 600 }}>Prevoz</label>
              <InputText
                value={form.transporter}
                onChange={(e) =>
                  setForm(prev => ({ ...prev, transporter: e.target.value }))
                }
                className="w-full"
              />
            </div>
          </div>

          <div className="col-12 md:col-6">
            <div className="field">
              <label style={{ fontWeight: 600 }}>Referenca</label>
              <InputText
                value={form.reference}
                onChange={(e) =>
                  setForm(prev => ({ ...prev, reference: e.target.value }))
                }
                className="w-full"
              />
            </div>
          </div>
          <div className="col-12">
            <div className="field">
              <label style={{ fontWeight: 600 }}>Napomene</label>
              <InputTextarea
                autoResize
                rows={3}
                value={form.notes}
                onChange={(e) =>
                  setForm(prev => ({ ...prev, notes: e.target.value }))
                }
                className="w-full"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-content-end gap-2 mt-3">
          <Button label="Sačuvaj" icon="pi pi-check" onClick={handleSubmit} />
          <Button
            label="Otkaži"
            icon="pi pi-times"
            severity="secondary"
            outlined
            onClick={() => setDialogVisible(false)}
          />
        </div>
      </Dialog>
    </div>
  );
};

export default HiltiMaterialReceipts;


